import {
    ArcByAngles,
    ArcByEdge,
    FollowsGeoBorder,
    GreatCircle,
    LineSegment,
    LineSegmentType,
} from "../../../core/geo/types";
import { isEmptyArray, isNotEmptyArray } from "../../../core/utils/array";
import { Position } from "geojson";
import { booleanClockwise } from "../../../common/turf/geoSpatialAnalysis";
import { circularArc } from "../../../common/util/draw/lineSegment/arc";

interface SegmentMapper {
    mapSegment: (segment: LineSegment) => Position;
}

export const getArcByAnglesCoordinates = (segment: LineSegment<ArcByAngles>) => {
    const { center: centerPoint, radius, bearing1, bearing2, point, endPoint } = segment.properties;
    const arcGeometry = circularArc(centerPoint, radius, bearing1, bearing2).geometry;
    if (!arcGeometry || isEmptyArray(arcGeometry.coordinates)) {
        throw Error("Failed to create arc");
    }
    const p1 = point ? point : arcGeometry.coordinates[0];
    const p2 = endPoint ? endPoint : arcGeometry.coordinates[arcGeometry.coordinates.length - 1];
    const isClockWise = booleanClockwise([...arcGeometry.coordinates, arcGeometry.coordinates[0]]);
    return isClockWise ? p1 : p2;
};

const mapSegmentToCoordinates = (segment: LineSegment) => {
    const coordinates = segmentMappers[segment.type].mapSegment(segment);
    if (coordinates) {
        return coordinates;
    }
    throw Error(`Failed to map segment ${JSON.stringify(segment)} to coordinates`);
};

const arcByAnglesMapper: SegmentMapper = {
    mapSegment(segment: LineSegment) {
        if (segment.properties.point && isNotEmptyArray(segment.properties.point)) {
            return segment.properties.point;
        }
        const point = getArcByAnglesCoordinates(segment as LineSegment<ArcByAngles>);
        segment.properties.point = point;
        return point;
    },
};

const defaultMapper: SegmentMapper = {
    mapSegment(segment: LineSegment) {
        return segment.properties.point;
    },
};

const segmentMappers: { [key in LineSegmentType]: SegmentMapper } = {
    GreatCircle: defaultMapper,
    ArcByEdge: defaultMapper,
    ArcByAngles: arcByAnglesMapper,
    FollowsGeoBorder: defaultMapper,
};

export const mapSegmentsToCoordinates = (
    segments: LineSegment<GreatCircle | ArcByAngles | ArcByEdge | FollowsGeoBorder>[] | undefined
) => {
    if (!isEmptyArray(segments)) {
        return segments!
            .sort((segment, segment2) => segment.properties.index - segment2.properties.index)
            .map((segment) => mapSegmentToCoordinates(segment));
    }
    return [];
};
